import { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { SurveySession } from "../typings";
import { useQuery } from "./useQuery";
import { useAuth } from "../context/AuthContext";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../conf/firebaseConfig";
import { genericConverter } from "../services/firestoreService";

interface UseGetSurveySessionRealTimeOptions {
  enabled?: boolean;
}

interface KeyedSurveySession {
  accessKey: string;
  data: Partial<SurveySession> | null;
}

export const useGetSurveySessionRealTime = (
  _surveySessionId?: string,
  options?: UseGetSurveySessionRealTimeOptions
) => {
  const urlQuery = useQuery();
  const surveySessionId =
    _surveySessionId ?? urlQuery.get("surveySessionId") ?? "";
  const { currentUser } = useAuth();
  const enabled = options?.enabled ?? true;
  const accessKey = `${surveySessionId ? `id:${surveySessionId}` : "latest"}:${
    currentUser?.uid ?? ""
  }`;
  const [keyedSurveySession, setKeyedSurveySession] =
    useState<KeyedSurveySession | null>(null);
  const surveySession =
    keyedSurveySession?.accessKey === accessKey
      ? keyedSurveySession.data
      : null;

  useEffect(() => {
    let active = true;
    setKeyedSurveySession({ accessKey, data: null });

    if (!enabled || !currentUser?.uid) {
      return () => {
        active = false;
      };
    }

    const handleError = (error: Error & { code?: string }) => {
      if (!active) return;
      setKeyedSurveySession({ accessKey, data: null });
      console.error("Error fetching real-time updates: ", error);
      datadogRum.addError(error, {
        hook: "useGetSurveySessionRealTime",
        collection: "surveySessions",
        surveySessionId: surveySessionId || "query",
        userId: currentUser.uid,
        errorCode: error.code,
        errorType: "firestore_listener_error",
      });
    };

    if (surveySessionId) {
      const docRef = doc(db, "surveySessions", surveySessionId).withConverter(
        genericConverter<Partial<SurveySession>>()
      );
      const unsubscribe = onSnapshot(
        docRef,
        (document) => {
          if (!active) return;
          if (document.exists()) {
            setKeyedSurveySession({
              accessKey,
              data: { id: document.id, ...document.data() },
            });
          } else {
            setKeyedSurveySession({ accessKey, data: null });
            console.log("No such document!");
          }
        },
        handleError
      );

      return () => {
        active = false;
        unsubscribe();
      };
    }

    const sessionsQuery = query(
      collection(db, "surveySessions"),
      where("userId", "==", currentUser.uid),
      orderBy("createdAt", "desc"),
      limit(1)
    ).withConverter(genericConverter<Partial<SurveySession>>());
    const unsubscribe = onSnapshot(
      sessionsQuery,
      (querySnapshot) => {
        if (!active) return;
        const sessions = querySnapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));
        setKeyedSurveySession({ accessKey, data: sessions[0] ?? null });
      },
      handleError
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [accessKey, currentUser?.uid, enabled, surveySessionId]);

  return {
    surveySession,
    medication: surveySession?.user?.medication,
    hasSurveySessionId: !!surveySessionId,
  };
};
